import {
  HIRING_AUTHORITY_DRAWERS,
  HIRING_AUTHORITY_PROFILE_CONFIG,
  HIRING_AUTHORITY_TAB_IDS
} from 'features/inventory-profiles/constants';
import { buildTabDataTableProps, getCommonTabs } from 'features/inventory-profiles/utils';
import strings from 'strings';
import CompaniesTab from 'UI/components/organisms/inventoryProfiles/CompaniesTab';
import FeeAgreementTab from 'UI/components/organisms/inventoryProfiles/FeeAgreementTab';
import HiringAuthoritiesOverview from 'UI/components/organisms/inventoryProfiles/HiringAuthoritiesOverview';
import JobOrdersTab from 'UI/components/organisms/inventoryProfiles/JobOrdersTab';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import { TasksTab } from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { EntityType } from 'UI/constants/entityTypes';

import { HIRING_AUTHORITY_TABS_CONFIG } from './HiringAuthorityProfileV2.constants';

const COPIES = strings.inventoryProfiles;

export const buildHiringAuthorityTabs = ({
  hiringAuthority,
  hiringAuthorityName,
  isLoading,
  openItemDrawer,
  permissions,
  refresh,
  refreshTab,
  searchProjectsTab
}) => {
  const profileEndpoint = `${HIRING_AUTHORITY_PROFILE_CONFIG.endpoint}/${hiringAuthority.id}`;
  const {
    [HIRING_AUTHORITY_TAB_IDS.Companies]: companies,
    [HIRING_AUTHORITY_TAB_IDS.Sendouts]: sendouts,
    [HIRING_AUTHORITY_TAB_IDS.JobOrders]: jobOrders,
    [HIRING_AUTHORITY_TAB_IDS.Placements]: placements,
    [HIRING_AUTHORITY_TAB_IDS.FeeAgreements]: feeAgreements,
    [HIRING_AUTHORITY_TAB_IDS.ActionPlan]: actionPlan
  } = HIRING_AUTHORITY_TABS_CONFIG;
  const profileTableProps = tabConfig => buildTabDataTableProps({ profileEndpoint, tabConfig });

  return [
    {
      id: HIRING_AUTHORITY_TAB_IDS.Overview,
      label: COPIES.common.tabs.overview,
      visible: true,
      content: <HiringAuthoritiesOverview loading={isLoading} profile={hiringAuthority} />
    },
    {
      id: HIRING_AUTHORITY_TAB_IDS.Companies,
      label: COPIES.common.tabs.companies,
      visible: true,
      content: (
        <CompaniesTab
          DataTableProps={profileTableProps(companies)}
          endpoint={profileEndpoint}
          onCompaniesChanged={refresh}
          profileId={hiringAuthority.id}
          profileName={hiringAuthorityName}
          tablePreferencesKey={companies.tablePreferencesKey}
        />
      )
    },
    {
      id: HIRING_AUTHORITY_TAB_IDS.Sendouts,
      label: COPIES.common.tabs.sendouts,
      visible: permissions.canSeeSendouts,
      content: (
        <SendoutsTab
          DataTableProps={profileTableProps(sendouts)}
          extraRenderers={sendouts.extraRenderers}
          initialColumns={sendouts.initialColumns}
          onSendoutClick={openItemDrawer(HIRING_AUTHORITY_DRAWERS.Sendout)}
          paramsBuilder={sendouts.paramsBuilder}
          profile={hiringAuthority}
          profileModule={EntityType.Candidate}
          tablePreferencesKey={sendouts.tablePreferencesKey}
        />
      )
    },
    {
      id: HIRING_AUTHORITY_TAB_IDS.JobOrders,
      label: COPIES.common.tabs.jobOrders,
      visible: permissions.canSeeJobOrders,
      content: (
        <JobOrdersTab
          DataTableProps={profileTableProps(jobOrders)}
          initialColumns={jobOrders.initialColumns}
          itemName={hiringAuthorityName}
          mode={EntityType.HiringAuthority}
          paramsBuilder={jobOrders.paramsBuilder}
          profileId={hiringAuthority.id}
          tablePreferencesKey={jobOrders.tablePreferencesKey}
        />
      )
    },
    {
      id: HIRING_AUTHORITY_TAB_IDS.Placements,
      label: COPIES.common.tabs.placements,
      visible: permissions.canSeePlacements,
      content: (
        <PlacementsTab
          DataTableProps={profileTableProps(placements)}
          initialColumns={placements.initialColumns}
          isLoading={isLoading}
          onTriggerTabRefresh={refreshTab}
          paramsBuilder={placements.paramsBuilder}
          profileId={hiringAuthority.id}
          profileModule={EntityType.HiringAuthority}
          tablePreferencesKey={placements.tablePreferencesKey}
        />
      )
    },
    {
      id: HIRING_AUTHORITY_TAB_IDS.FeeAgreements,
      label: COPIES.common.tabs.feeAgreements,
      visible: permissions.canSeeFeeAgreements,
      content: (
        <FeeAgreementTab
          DataTableProps={profileTableProps(feeAgreements)}
          extraRenderers={feeAgreements.extraRenderers}
          initialColumns={feeAgreements.initialColumns}
          paramsBuilder={feeAgreements.paramsBuilder}
          profile={hiringAuthority}
          profileModule={EntityType.HiringAuthority}
          tablePreferencesKey={feeAgreements.tablePreferencesKey}
        />
      )
    },
    {
      id: HIRING_AUTHORITY_TAB_IDS.ActionPlan,
      label: COPIES.sections.tabs.actionPlan.tabLabel,
      visible: permissions.canUseDailyPlan,
      content: (
        <TasksTab
          allowedTaskTypes={actionPlan.allowedTaskTypes}
          DataTableProps={actionPlan.DataTableProps}
          entityType={actionPlan.entityType}
          entityTypeId={actionPlan.entityTypeId}
          extraRenderers={actionPlan.extraRenderers}
          initialColumns={actionPlan.initialColumns}
          itemName={hiringAuthorityName}
          profileId={hiringAuthority.id}
          tablePreferencesKey={actionPlan.tablePreferencesKey}
        />
      )
    },
    ...getCommonTabs({
      profileId: hiringAuthority.id,
      entity: HIRING_AUTHORITY_PROFILE_CONFIG,
      itemName: hiringAuthorityName,
      onActivityComplete: refresh,
      searchProjectsTab
    })
  ];
};
